// music: each person's own library of songs, played back in the app. songs come from uploaded files or from youtube links
// the server downloads for them. a library is a list of tracks and a list of playlists

export const MAX_TRACKS = 2000;
export const MAX_PLAYLISTS = 60;
export const MAX_PLAYLIST_IMPORT = 40;
export const MAX_UPLOAD_BYTES = 60_000_000;
export const MAX_COVER_BYTES = 1_500_000;
// the longest video the server will download
export const MAX_VIDEO_SECONDS = 3600;

export const AUDIO_EXTENSIONS = ["mp3", "m4a", "aac", "ogg", "oga", "opus", "wav", "flac", "webm"] as const;

export const AUDIO_FILE = /^[a-f0-9]{8,40}\.(mp3|m4a|aac|ogg|oga|opus|wav|flac|webm)$/;
export const COVER_FILE = /^[a-f0-9]{8,40}-cover\.(jpg|webp|png)$/;

export interface Track {
  id: string;
  title: string;
  artist: string;
  album: string;
  // the audio file in this person's music folder
  file: string;
  // the cover picture in the same folder, empty when there is none and the app draws a label from the title
  cover: string;
  // seconds, 0 when not known yet
  duration: number;
  source: "upload" | "youtube";
  sourceUrl: string;
  addedAt: string;
  // added on the way out
  audioUrl?: string;
  coverUrl?: string;
}

export interface Playlist {
  id: string;
  name: string;
  trackIds: string[];
}

export interface MusicLibrary {
  tracks: Track[];
  playlists: Playlist[];
}

export interface MusicJob {
  id: string;
  url: string;
  state: "queued" | "running" | "done" | "failed";
  // what to show: the title once known, or why it failed
  message: string;
  at: string;
}

export interface MusicTools {
  ytdlp: boolean;
  ffmpeg: boolean;
}

export interface TrackEdit {
  id: string;
  title?: string;
  artist?: string;
  album?: string;
  duration?: number;
  // a new cover as a data url, or true to take the cover away
  cover?: string | boolean;
}

export interface PlaylistEdit {
  id?: string;
  name: string;
  trackIds: string[];
}

const ID = /^[a-z0-9]{4,24}$/i;

function text(value: unknown, max: number): string {
  return String(value ?? "").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, max);
}

export function cleanTrack(raw: unknown, fallbackId: string): Track | null {
  const given = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const file = String(given.file ?? "");
  if (!AUDIO_FILE.test(file)) return null;
  const id = ID.test(String(given.id ?? "")) ? String(given.id) : fallbackId;
  const cover = String(given.cover ?? "");
  const duration = Number(given.duration);
  return {
    id,
    title: text(given.title, 160) || "untitled",
    artist: text(given.artist, 120),
    album: text(given.album, 120),
    file,
    cover: COVER_FILE.test(cover) ? cover : "",
    duration: Number.isFinite(duration) && duration > 0 && duration < 86400 ? Math.round(duration) : 0,
    source: given.source === "youtube" ? "youtube" : "upload",
    sourceUrl: given.source === "youtube" ? String(given.sourceUrl ?? "").slice(0, 300) : "",
    addedAt: String(given.addedAt ?? "") || new Date().toISOString()
  };
}

export function cleanPlaylist(raw: unknown, known: Set<string>, fallbackId: string): Playlist {
  const given = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const seen = new Set<string>();
  const trackIds = (Array.isArray(given.trackIds) ? given.trackIds : []).map(String).filter((id) => known.has(id) && !seen.has(id) && seen.add(id)).slice(0, MAX_TRACKS);
  return { id: ID.test(String(given.id ?? "")) ? String(given.id) : fallbackId, name: text(given.name, 60) || "playlist", trackIds };
}

// a youtube link the server may download. anything else is refused before yt-dlp ever sees it
const YOUTUBE_HOSTS = ["youtube.com", "www.youtube.com", "m.youtube.com", "music.youtube.com", "youtu.be"];

export function parseYoutube(raw: string): { kind: "video" | "playlist"; url: string } | null {
  let url: URL;
  try {
    url = new URL(String(raw).trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (!YOUTUBE_HOSTS.includes(url.hostname.toLowerCase()) || url.username || url.password) return null;
  const video = url.hostname === "youtu.be" ? url.pathname.slice(1) : url.pathname.startsWith("/shorts/") ? url.pathname.split("/")[2] : url.searchParams.get("v") || "";
  if (/^[\w-]{11}$/.test(video || "")) return { kind: "video", url: `https://www.youtube.com/watch?v=${video}` };
  const list = url.searchParams.get("list") || "";
  if (url.pathname === "/playlist" && /^[\w-]{10,64}$/.test(list)) return { kind: "playlist", url: `https://www.youtube.com/playlist?list=${list}` };
  return null;
}

// "Artist - Title" from a file name, when the file carries no tags
export function namesFromFile(name: string): { title: string; artist: string } {
  const base = name.replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").trim();
  const split = base.split(/\s+-\s+/);
  if (split.length >= 2) return { artist: text(split[0], 120), title: text(split.slice(1).join(" - "), 160) };
  return { artist: "", title: text(base, 160) };
}
